/**
 * Image preloading utility for optimizing image loading and caching
 */

interface PreloadOptions {
  priority?: 'high' | 'low';
  cache?: boolean;
}

class ImagePreloader {
  private preloadedImages: Set<string> = new Set();
  private cacheName = 'artwork-images';

  /**
   * Preload a single image
   */
  async preloadImage(
    src: string,
    options: PreloadOptions = {}
  ): Promise<void> {
    const { priority = 'low', cache = true } = options;
    const fullSrc = src.startsWith('http') ? src : `${window.location.origin}${src}`;

    // Skip if already preloaded
    if (this.preloadedImages.has(fullSrc)) {
      return;
    }

    try {
      // Use link preload for high priority images
      if (priority === 'high') {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = fullSrc;
        document.head.appendChild(link);
      }

      // Preload using Image object
      const img = new Image();
      img.src = fullSrc;
      
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          this.preloadedImages.add(fullSrc);
          resolve();
        };
        img.onerror = reject;
      });

      // Cache in Cache API if available
      if (cache && 'caches' in window) {
        try {
          const cache = await caches.open(this.cacheName);
          const cached = await cache.match(fullSrc);
          if (!cached) {
            const response = await fetch(fullSrc, { mode: 'cors' });
            if (response.ok) {
              await cache.put(fullSrc, response);
            }
          }
        } catch (error) {
          // Silently fail caching
        }
      }
    } catch (error) {
      // Silently fail preloading
      console.debug('Image preload failed:', fullSrc);
    }
  }

  /**
   * Preload multiple images with priority handling
   */
  async preloadImages(
    images: string[],
    options: PreloadOptions = {}
  ): Promise<void> {
    const { priority = 'low' } = options;

    // Use requestIdleCallback for low priority, immediate for high priority
    const preloadBatch = async (): Promise<void> => {
      const promises = images.map((src) => this.preloadImage(src, options));
      
      if (priority === 'high') {
        await Promise.all(promises);
      } else {
        // Load in batches to avoid blocking
        const batchSize = 3;
        for (let i = 0; i < promises.length; i += batchSize) {
          const batch = promises.slice(i, i + batchSize);
          await Promise.all(batch);
          // Small delay between batches
          if (i + batchSize < promises.length) {
            await new Promise((resolve) => setTimeout(resolve, 50));
          }
        }
      }
    };

    if (priority === 'high') {
      await preloadBatch();
    } else if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        void preloadBatch();
      }, { timeout: 2000 });
    } else {
      setTimeout(() => {
        void preloadBatch();
      }, 100);
    }
  }

  /**
   * Preload thumbnails for popular artworks (for home page)
   */
  async preloadThumbnails(artworks: Array<{ thumbnailUrl?: string; imageUrl: string }>): Promise<void> {
    const thumbnailUrls = artworks
      .map((artwork) => artwork.thumbnailUrl || artwork.imageUrl)
      .filter((url) => url && !this.preloadedImages.has(url));

    if (thumbnailUrls.length === 0) return;

    // Preload first 6 immediately (above the fold), rest lazily
    const immediate = thumbnailUrls.slice(0, 6);
    const lazy = thumbnailUrls.slice(6);

    // Preload immediate ones with high priority
    await this.preloadImages(immediate, { priority: 'high', cache: true });

    // Preload rest with low priority
    if (lazy.length > 0) {
      this.preloadImages(lazy, { priority: 'low', cache: true });
    }
  }

  /**
   * Check if image is already cached
   */
  async isCached(src: string): Promise<boolean> {
    if (!('caches' in window)) return false;

    try {
      const cache = await caches.open(this.cacheName);
      const fullSrc = src.startsWith('http') ? src : `${window.location.origin}${src}`;
      const cached = await cache.match(fullSrc);
      return !!cached;
    } catch {
      return false;
    }
  }

  /**
   * Clear image cache
   */
  async clearCache(): Promise<void> {
    if ('caches' in window) {
      try {
        await caches.delete(this.cacheName);
        this.preloadedImages.clear();
      } catch (error) {
        console.error('Failed to clear image cache:', error);
      }
    }
  }
}

export const imagePreloader = new ImagePreloader();
